import {
  markedFulfilledTypeToJSON,
  orderItemCancellationTypeToJSON,
} from '@classpass/protobuf-definitions/lib/web/models/shared/orders/sku_order';

import { normalizeMoney } from '../Orders/SkuOrders.normalizers';
import { enumToFeString, toFeLifecycleStatus } from '../shared/SkuEnumHelpers';

const normalizePeriod = period => ({
  earnings: (period?.totalPayableAmount || []).map(normalizeMoney).filter(Boolean),
  ordersCount: Number(period?.ordersCount) || 0,
  cancelledOrdersCount: Number(period?.cancelledOrdersCount) || 0,
});

export const normalizeOrdersReportSummary = summary => ({
  current: normalizePeriod(summary?.current),
  lastPeriod: normalizePeriod(summary?.lastPeriod),
});

const toIsoString = value => (value instanceof Date ? value.toISOString() : value || null);

// The report flattens each item's modifier groups into one list; SkuOrderFlyout reads the
// orders page shape (groups of modifiers), so the list is wrapped back into a single group.
const toModifierGroups = modifiers =>
  modifiers?.length
    ? [
        {
          orderModifierGroupId: null,
          orderModifiers: modifiers.map(modifier => ({
            orderModifierId: modifier.orderModifierId,
            modifierSummary: { modifierId: modifier.modifierId || null, name: modifier.name || null },
            nestedModifierGroups: toModifierGroups(modifier.modifiers),
          })),
        },
      ]
    : [];

const toOrderItem = item => ({
  core: {
    orderItemId: item.orderItemId,
    cancelledAt: toIsoString(item.cancelledAt),
    cancellationType: enumToFeString(item.cancellationType, orderItemCancellationTypeToJSON),
    itemSummary: { itemId: item.itemId || null, name: item.name || null },
  },
  modifierGroups: toModifierGroups(item.modifiers),
  payableAmount: normalizeMoney(item.payableAmount),
  basePayableAmount: normalizeMoney(item.basePayableAmount),
});

// Rows come back in the PartnerOrder shape the orders page uses, so the same helpers build
// the table cells and SkuOrderFlyout renders the row as is.
const normalizeOrdersReportOrder = order => ({
  core: {
    orderId: order.orderId,
    venueId: order.venueId,
    createdOn: toIsoString(order.placedAt),
    requestedPickupTime: toIsoString(order.requestedPickupTime),
    userMarkedPickupAt: toIsoString(order.userMarkedPickupAt),
    markedFulfilledAt: toIsoString(order.markedFulfilledAt),
    markedFulfilledType: enumToFeString(order.markedFulfilledType, markedFulfilledTypeToJSON),
  },
  orderItems: (order.items || []).map(toOrderItem),
  userName: order.customerName || null,
  venueName: order.venueName || null,
  lifecycleStatus: toFeLifecycleStatus(order.lifecycleStatus),
  payableAmount: normalizeMoney(order.payableAmount),
});

export const normalizeOrdersReport = report => ({
  orders: (report?.orders || []).map(normalizeOrdersReportOrder),
  nextPageCursor: report?.nextPageCursor || null,
  totalCount: Number(report?.totalCount) || 0,
});
